import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import GlowingOrb from "../effects/GlowingOrb";
import { useLanguage } from "@/context/LanguageContext";

const defaultContent = {
  section_label: "Testimonios",
  title: "Lo que Dicen Nuestros Clientes",
  subtitle: "Empresas de todos los sectores confían en nosotros para impulsar su transformación digital.",
  testimonials: [
    { id: "1", name: "Carlos Rodríguez", company: "Logística Express", role: "Director de Operaciones", content: "Global Metafora transformó completamente nuestra gestión de envíos. El sistema es intuitivo, rápido y ha reducido nuestros tiempos de entrega en un 35%.", rating: 5 },
    { id: "2", name: "María González", company: "TechRetail Solutions", role: "Gerente General", content: "Implementamos el sistema hace 2 años y hemos aumentado nuestra eficiencia en un 40%. La mejor inversión tecnológica que hemos hecho.", rating: 5 },
    { id: "3", name: "Roberto Sánchez", company: "Finanzas Plus", role: "CEO", content: "La seguridad y confidencialidad que ofrece Global Metafora es incomparable. Nuestros datos financieros están protegidos al máximo nivel.", rating: 5 }
  ]
};

const TestimonialsSection = ({ content }) => {
  const data = { ...defaultContent, ...content };
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const { t } = useLanguage();

  const testimonials = data.testimonials;

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setActiveIndex((prev) => {
      if (newDirection === 1) return (prev + 1) % testimonials.length;
      return (prev - 1 + testimonials.length) % testimonials.length;
    });
  };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0, scale: 0.9 }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (dir) => ({ x: dir > 0 ? -300 : 300, opacity: 0, scale: 0.9 }),
  };

  return (
    <section
      id="testimonios"
      ref={sectionRef}
      data-testid="testimonials-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Background */}
      <GlowingOrb color="#3b82f6" size={500} className="top-1/4 -left-40" />
      <GlowingOrb color="#8b5cf6" size={400} className="bottom-1/4 -right-40" />

      <div className="section-container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            className="text-[#3b82f6] text-sm font-semibold tracking-wider uppercase mb-4 inline-flex items-center gap-2"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
          >
            <motion.span
              className="w-8 h-px bg-[#3b82f6]"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.3, duration: 0.5 }}
            />
            {t("testimonials_label")}
            <motion.span
              className="w-8 h-px bg-[#3b82f6]"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.3, duration: 0.5 }}
            />
          </motion.span>
          <motion.h2
            data-testid="testimonials-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            {t("testimonials_title")}
          </motion.h2>
          <motion.p className="text-[#94a3b8] text-lg max-w-2xl mx-auto">
            {t("testimonials_subtitle")}
          </motion.p>
          <motion.p className="text-[#3b82f6]/50 text-sm font-semibold tracking-widest uppercase mt-3">
            {t("testimonials_brand")}
          </motion.p>
        </motion.div>
        <div className="max-w-3xl mx-auto mb-12 relative">
          <div className="relative overflow-hidden min-h-[280px]">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={activeIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                className="glass-card p-8 md:p-12 relative overflow-hidden"
              >
                {/* Decorative quote */}
                <motion.div
                  className="absolute top-4 right-4 opacity-10"
                  animate={{ rotate: [0, 5, 0, -5, 0] }}
                  transition={{ duration: 6, repeat: Infinity }}
                >
                  <Quote className="w-24 h-24 text-[#3b82f6]" />
                </motion.div>

                {/* Animated gradient accent */}
                <motion.div
                  className="absolute top-0 left-0 w-full h-1"
                  style={{
                    background: "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
                    backgroundSize: "300% 100%"
                  }}
                  animate={{ backgroundPosition: ["0% 0%", "300% 0%"] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                />

                <p className="text-white text-lg md:text-xl leading-relaxed mb-8 relative z-10">
                  "{testimonials[activeIndex].content}"
                </p>

                {/* Stars with sparkle */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                    </motion.div>
                  ))}
                </div>

                <div className="flex items-center gap-4">
                  <motion.div
                    className="w-14 h-14 bg-gradient-to-br from-[#3b82f6] to-[#06b6d4] flex items-center justify-center text-white font-bold text-xl rounded-sm"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    {testimonials[activeIndex].name.charAt(0)}
                  </motion.div>
                  <div>
                    <p className="text-white font-semibold text-lg">{testimonials[activeIndex].name}</p>
                    <p className="text-[#94a3b8] text-sm">{testimonials[activeIndex].role}</p>
                    <p className="text-[#3b82f6] text-sm font-medium">{testimonials[activeIndex].company}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <motion.button
              data-testid="testimonial-prev"
              onClick={() => paginate(-1)}
              className="w-12 h-12 glass-card flex items-center justify-center text-[#94a3b8] hover:text-white hover:border-[#3b82f6]/50 transition-all"
              whileHover={{ scale: 1.1, x: -3 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft className="w-5 h-5" />
            </motion.button>

            {/* Dots indicator */}
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <motion.button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => { setDirection(i > activeIndex ? 1 : -1); setActiveIndex(i); }}
                  className="relative"
                  whileHover={{ scale: 1.2 }}
                >
                  <motion.div
                    className="w-2 h-2 rounded-full"
                    animate={{
                      backgroundColor: i === activeIndex ? "#3b82f6" : "#4b5563",
                      scale: i === activeIndex ? 1.5 : 1,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  {i === activeIndex && (
                    <motion.div
                      className="absolute inset-0 rounded-full bg-[#3b82f6]"
                      initial={{ scale: 1 }}
                      animate={{ scale: [1, 2.5, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </motion.button>
              ))}
            </div>

            <motion.button
              data-testid="testimonial-next"
              onClick={() => paginate(1)}
              className="w-12 h-12 glass-card flex items-center justify-center text-[#94a3b8] hover:text-white hover:border-[#3b82f6]/50 transition-all"
              whileHover={{ scale: 1.1, x: 3 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        {/* Small cards grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              data-testid={`testimonial-card-${testimonial.id}`}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 + index * 0.15 }}
              className={`glass-card p-6 cursor-pointer transition-all duration-300 ${
                index === activeIndex ? "border-[#3b82f6]/50 shadow-[0_0_30px_rgba(59,130,246,0.15)]" : ""
              }`}
              onClick={() => { setDirection(index > activeIndex ? 1 : -1); setActiveIndex(index); }}
              whileHover={{ y: -5, borderColor: "rgba(59,130,246,0.4)" }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: `${["#3b82f6", "#06b6d4", "#8b5cf6"][index]}20`, color: ["#3b82f6", "#06b6d4", "#8b5cf6"][index] }}
                >
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{testimonial.name}</p>
                  <p className="text-[#3b82f6] text-xs">{testimonial.company}</p>
                </div>
              </div>
              <p className="text-[#94a3b8] text-sm line-clamp-2">"{testimonial.content}"</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
